import { toTitleCase } from '../formatCustomerData';
import type {
  EnrichedOrderHistory,
  Product,
  ProductImage
} from '../../services/react-query/query/orders/types';

/**
 * Order items carry `images` as bare path strings, while the catalog stores
 * objects (`{_id, productId, path}`) and the `/enriched` endpoint passes those
 * objects straight through. Normalize to the catalog shape so shared product
 * components can read `.path` no matter which endpoint the order came from.
 */
export const toProductImages = (images: Product['images']): ProductImage[] =>
  (images || [])
    .map(image => (typeof image === 'string' ? { path: image } : image))
    .filter(image => !!image?.path);

/** The first image as a URL, whichever of the two shapes it arrived in. */
export const getOrderProductImageUrl = (images: Product['images']): string =>
  toProductImages(images)[0]?.path || '';

/**
 * An order item's product, in the shape the catalog components expect.
 *
 * `/order-items/account/:accountNumber` used to return seven fields frozen at
 * the moment the order was placed, so everything else was filled in with empty
 * values here. It now serves the catalog's product, reloaded on every request
 * (DEV-689), so those fields are read from the response and only the handful
 * the endpoint still doesn't send keep their empty fallbacks.
 */
export const mapOrderToProduct = <T = Record<string, any>>(
  order: EnrichedOrderHistory,
  overrides?: Partial<T>
): T => {
  const product = order?.product;
  const images = toProductImages(product?.images);

  return {
    active: product?.active ?? true,
    discontinued: product?.discontinued || false,
    categories: product?.categories || [],
    created_on: '',
    description: product?.description || '',
    dropship: false,
    // Current stock, not stock as of the order date — never label it otherwise.
    inventory: product?.inventory || [],
    last_modified: '',
    manufacturerProductNumber: '',
    packSize: '',
    procurementStopped: false,
    productCode: '',
    promotions: product?.promotions || [],
    salesStopped: false,
    // Read from the response since DEV-689. Without it a non-stocked product
    // falls through to the red out-of-stock label instead of the hyphen, and
    // the "Stocked / Not Stocked" badge can't be drawn at all — so keep the
    // null fallback for older responses that omit it rather than inventing one.
    stockInfo: product?.stockInfo || null,
    isStockProduct: product?.isStockProduct,
    special: product?.special || false,
    unit: '',
    unitOfMeasure: product?.unitOfMeasure || '',
    updated_on: '',
    vendor: product?.vendor,
    custom: product?.custom || null,
    privateLabel: false,
    productAssociations: product?.productAssociations || [],
    images,
    // eslint-disable-next-line no-underscore-dangle
    _id: product?._id,
    sku: product?.sku,
    quantity: undefined,
    price: null,
    cost: order.cost,
    sell: order.sell,
    profitMarginCurrency: order.sell - order.cost || 0,
    profitMarginPercent: order.sell
      ? parseFloat((((order.sell - order.cost) / order.sell) * 100).toFixed(2))
      : 0,
    manufacturerSku: product?.manufacturerSku,
    imageUrl: images[0]?.path || '',
    name: toTitleCase(product?.name),
    needsPriceUpdate: false,
    loading: false,
    isEditable: false,
    createdDate: order.createdDate,
    lastSoldQuantity: order.quantity,
    ...(overrides || {})
  } as T;
};
